import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroSpotlightSwap.css";

/**
 * HeroSpotlightSwap — animation-first family where one photo
 * dominates the media area ("the spotlight") with the rest queued
 * for rotation; on a slow JS interval the spotlight rotates through
 * the queue.
 *
 * Two layout variants share this renderer (dispatched from
 * ``HeroAutomatic.js``):
 *
 *   * ``spotlight_swap``       — canonical horizontal split. Photo
 *                                column on one side (~55% width on
 *                                desktop), text column on the other,
 *                                thumbnail strip beneath the
 *                                spotlight tile.
 *   * ``spotlight_swap_stack`` — vertical stack. Photo on top
 *                                spanning the full hero width with
 *                                the text column stacked beneath
 *                                full-width. Replaces the thumbnail
 *                                strip with a *clickable* dot
 *                                indicator overlaid on the bottom
 *                                edge of the photo so visitors can
 *                                jump to a specific photo;
 *                                auto-rotation pauses briefly after
 *                                a click then resumes (see
 *                                ``CLICK_PAUSE_RESUME_MS`` /
 *                                ``useSpotlightRotation``).
 *   * ``spotlight_swap_stack_flush`` — same vertical stack, but the
 *                                photo is pressed flush against the
 *                                hero's top + side edges. Strips the
 *                                stage's rounded corners, drop
 *                                shadow, and inner gap padding, and
 *                                breaks out of the hero root's
 *                                vertical spacing so the photo butts
 *                                straight up against the hero top
 *                                edge. Same dot-overlay affordance
 *                                as the canonical stack variant —
 *                                only the chrome around the photo
 *                                differs.
 *
 * Why JS instead of CSS keyframes for the rotation: the swap needs
 * to coordinate two different elements at once (the previous
 * spotlight fades out *while* the new one fades in, and the
 * corresponding thumbnails / dots swap their active highlight).
 * That cross-element timing isn't expressible as a single keyframe
 * group — JS state is the natural fit.
 *
 * Why click-to-navigate is stack-only (not horizontal): per the UX
 * decision when the family was extended, the canonical horizontal
 * variant keeps its auto-only thumbnail behaviour to preserve
 * existing user heroes' visual contract; the stack variant's dot
 * indicator is a deliberately new affordance for the new layout.
 * If we later decide to extend it to the horizontal variant, the
 * gating lives in a single helper (``isClickToNavigateVariant``)
 * for an easy flip.
 *
 * Edit-mode is treated as static: the swap interval would fight the
 * editor's own selection / hover indicators, and users editing the
 * gallery want predictable framing. The renderer detects edit mode
 * by the presence of an EditContext, same pattern other hero modes
 * use via ``SlotRenderer``.
 */

// Mode id of the vertical-stack layout sibling. Centralised in a
// single constant so the renderer's layout switches and the
// click-to-navigate gating can both reference the same name —
// avoids the bug class where one branch checks the id one way and
// another branch checks it differently and they silently diverge.
const STACK_MODE_ID = "spotlight_swap_stack";

// Flush (edge-to-edge) sibling of the stack layout. Same vertical
// composition + dot-overlay affordance as ``STACK_MODE_ID``; only
// the chrome around the photo differs (no rounded corners, no
// shadow, no gap padding) and the renderer pulls the inner column
// up to absorb the hero root's vertical padding so the photo
// butts straight against the top edge. Centralised here for the
// same reason as ``STACK_MODE_ID`` — the layout switches and the
// chrome-flip checks should all key off one constant.
const STACK_FLUSH_MODE_ID = "spotlight_swap_stack_flush";

// Per-breakpoint config for the canonical horizontal split. Owns
// media column width (as a % of the row), inter-column padding,
// and per-thumbnail size. The stack variant uses its own
// ``BP_STACK_CONFIG`` below — the two configs are kept separate
// (rather than merged into one config with extra fields) because
// the stack layout doesn't have a horizontal/vertical-on-small
// switch (it's vertical at every breakpoint), and conflating the
// two would force every reader to mentally branch on the layout
// just to read a width.
const BP_CONFIG = {
  extra_large: { horizontal: true, mediaPct: "55%", contentPx: 64, thumbSize: 88 },
  large:       { horizontal: true, mediaPct: "55%", contentPx: 56, thumbSize: 80 },
  medium:      { horizontal: true, mediaPct: "50%", contentPx: 40, thumbSize: 72 },
  small:       { horizontal: false, contentPx: 24, contentPy: 32, thumbSize: 64, mediaMaxH: "45vh" },
  extra_small: { horizontal: false, contentPx: 16, contentPy: 24, thumbSize: 56, mediaMaxH: "40vh" },
};

// Target shape for the canonical horizontal split's photo column.
//
// Without this the photo column simply stretches to the hero's
// ``baseHeightPx`` (default 500px) while sitting at ~55% width, so
// on desktop the crop reads as a wide letterbox (≈633×500 →
// noticeably wider than tall). Pinning a near-square aspect ratio
// keeps the column's *width* untouched (still ``mediaPct``) and
// instead grows its *height* so the photo cut reads square.
//
// Used together with ``alignSelf: "flex-start"`` (so the column
// sizes to this ratio rather than being force-stretched to the
// row height) and a ``baseHeightPx`` floor via ``minHeight`` (so
// the column never gets SHORTER than the configured hero height —
// on narrow viewports where 55%-of-width would fall below the
// hero height the floor wins and the crop simply becomes a tall
// portrait, which is still squarer than the old letterbox).
//
// Only applied in responsive-height mode: in fixed-height heroes
// the row is ``absolute inset-0`` and clamped to the hero box, so
// growing the column would overflow it — there we leave the
// original stretch behaviour untouched.
const HORIZONTAL_MEDIA_ASPECT = "1 / 1";

// Per-breakpoint config for the vertical-stack layout. The photo
// occupies a percentage of the hero height (not a fixed pixel
// height) so the proportions stay balanced as the hero resizes
// with the viewport.
//
// ``mediaHeightPct`` was previously 65/65/60/55/50 across
// breakpoints — that left a noticeable band of empty hero between
// the bottom of the photo and the heading. The Spotlight stack
// reads as a "photo-led" hero (photo dominates, text follows)
// and that needs the photo to actually dominate the canvas, not
// share it 60/40. Bumping each breakpoint by ~10pts gives the
// image meaningfully more vertical real estate while still
// leaving room for heading + body + CTA underneath.
//
// ``contentPy`` was previously 40/36/32/24/20 — roughly halving
// it tightens the visual coupling between the photo and the
// title beneath: at 40px on desktop the heading sat lower in the
// hero than it needed to, making the photo and title read as two
// disconnected zones rather than a single composition. The text
// section still has its own ``gap-3`` between heading / body /
// CTA so the inner rhythm stays breathable; we're only trimming
// the OUTER padding above the heading.
const BP_STACK_CONFIG = {
  extra_large: { mediaHeightPct: 75, contentPx: 64, contentPy: 20 },
  large:       { mediaHeightPct: 74, contentPx: 56, contentPy: 18 },
  medium:      { mediaHeightPct: 70, contentPx: 40, contentPy: 16 },
  small:       { mediaHeightPct: 64, contentPx: 24, contentPy: 12 },
  extra_small: { mediaHeightPct: 60, contentPx: 16, contentPy: 10 },
};

// Time between automatic spotlight rotations. Long enough that each
// photo gets a chance to be looked at properly; short enough that the
// page doesn't feel static after the first cycle.
const SWAP_INTERVAL_MS = 4500;

// How long auto-rotation stays paused after the visitor clicks a
// dot to jump to a specific photo. Long enough that the visitor
// has time to actually look at the photo they chose (otherwise the
// rotation would feel like it's fighting them — they pick photo
// 3, then ~4s later the photo is already gone). Short enough that
// inattention leaves the slideshow drifting forward on its own
// rather than silently freezing.
//
// 9000ms = roughly two SWAP_INTERVAL_MS ticks worth of dwell
// before the loop resumes from the manually-chosen index (the
// next tick after resume advances from there, not from the
// pre-click index, so a click effectively shifts the rotation's
// starting point).
const CLICK_PAUSE_RESUME_MS = 9000;

// Number of thumbnails before the swap behaviour engages. With a
// single image there's nothing to rotate, and with two images the
// swap reads as flicker rather than rotation.
const MIN_FOR_SWAP = 2;

// ---------------------------------------------------------------------------
// Layout-variant helpers
// ---------------------------------------------------------------------------

// Whether this mode id renders the vertical-stack layout. Returns
// true for both the canonical stack id AND its flush sibling —
// they share the same vertical composition, so both flow through
// the stack branch in the renderer. The ``_flush`` chrome flip is
// a separate orthogonal axis owned by ``isStackFlushVariant``
// below; conflating the two would either route the flush variant
// through the horizontal-split branch (silently loses the
// composition) or scatter the flush check across every layout
// switch.
function isStackVariant(modeId) {
  return modeId === STACK_MODE_ID || modeId === STACK_FLUSH_MODE_ID;
}

// Whether this mode id is the flush (edge-to-edge) chrome variant
// of the stack layout. Used by the renderer to flip three things
// in lockstep: drop the stage's rounded corners + drop shadow,
// zero the inner gap padding around the stage, and break out of
// the hero root's vertical padding so the photo butts straight
// against the hero top edge. Centralised so future flush variants
// (e.g. a hypothetical ``spotlight_swap_flush`` for the
// horizontal layout) only need one constant added to the family
// and one branch flipped here, not three scattered checks.
function isStackFlushVariant(modeId) {
  return modeId === STACK_FLUSH_MODE_ID;
}

// Whether this mode id exposes the visitor click-to-navigate
// affordance (clickable dots / thumbnails that jump to a specific
// photo and pause auto-rotation briefly). Currently equivalent to
// ``isStackVariant`` — the canonical horizontal sibling
// deliberately keeps the auto-only thumbnail behaviour. Lives as
// its own helper so the gating decision can be reread / extended
// independently of the layout decision (e.g. if we ever want
// click-to-navigate on the horizontal variant we flip this
// helper, not the layout switches).
function isClickToNavigateVariant(modeId) {
  return isStackVariant(modeId);
}

// ---------------------------------------------------------------------------
// useSpotlightRotation
// ---------------------------------------------------------------------------
//
// Drives the active spotlight index. Behaviours:
//
//   * Auto-rotates through ``[0, count)`` on a fixed interval.
//   * Pauses while ``count < MIN_FOR_SWAP`` (nothing to rotate),
//     while the user has prefers-reduced-motion enabled, while the
//     tab is hidden (so the swap doesn't burn cycles in background
//     tabs and doesn't surprise the user with a different photo
//     when they return), and while the caller has set
//     ``paused: true``.
//   * Exposes ``selectIndex(index)`` so click-to-navigate
//     consumers can jump to a specific photo. Calling ``selectIndex``
//     restarts the pause-then-resume timer (``resumeAfterMs``);
//     during that window the auto interval is held so the
//     rotation doesn't immediately overwrite the visitor's
//     chosen photo.

function useSpotlightRotation(
  count,
  { paused = false, resumeAfterMs = 0 } = {},
) {
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  // Tracks "auto-rotation is suspended right now because the
  // visitor just clicked a dot". Distinct from the ``paused``
  // prop which is owner-driven (e.g. paused while in edit mode);
  // this flag is internal to the hook because only the hook knows
  // about the click pause-resume timer.
  const [clickPaused, setClickPaused] = useState(false);
  // Refs avoid stale-closure bugs in the resume timer callback —
  // it needs to read the *current* count when it fires (which can
  // be different from the count when the timer was scheduled if
  // the gallery shrinks mid-pause).
  const indexRef = useRef(0);
  indexRef.current = spotlightIndex;
  const clickPauseTimerRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
    );
  }, []);

  // Reset to the first slide whenever the image list shrinks past the
  // current spotlight index (e.g. user removed the active image in
  // edit mode). Without this we'd render an empty spotlight until the
  // next interval tick.
  useEffect(() => {
    if (spotlightIndex >= count) {
      setSpotlightIndex(0);
    }
  }, [count, spotlightIndex]);

  // Cleanup any pending resume timer when the component unmounts
  // so we don't call ``setClickPaused`` after unmount (would
  // trigger a "set state on unmounted component" warning in React
  // and is a memory-leak in long-lived editor sessions).
  useEffect(() => {
    return () => {
      if (clickPauseTimerRef.current) {
        clearTimeout(clickPauseTimerRef.current);
        clickPauseTimerRef.current = null;
      }
    };
  }, []);

  const selectIndex = useCallback(
    (nextIndex) => {
      if (
        typeof nextIndex !== "number" ||
        nextIndex < 0 ||
        nextIndex >= count
      ) {
        // Defensive: silently ignore out-of-bounds clicks (can happen
        // if the gallery shrinks between the user's click and the
        // event reaching the handler; better to no-op than render an
        // empty spotlight).
        return;
      }
      setSpotlightIndex(nextIndex);
      // Only engage the pause window if a resume delay was actually
      // configured; without it, the click would freeze auto-
      // rotation forever which isn't what any current caller wants.
      if (resumeAfterMs > 0) {
        setClickPaused(true);
        if (clickPauseTimerRef.current) {
          clearTimeout(clickPauseTimerRef.current);
        }
        clickPauseTimerRef.current = setTimeout(() => {
          setClickPaused(false);
          clickPauseTimerRef.current = null;
        }, resumeAfterMs);
      }
    },
    [count, resumeAfterMs],
  );

  useEffect(() => {
    if (
      paused ||
      clickPaused ||
      prefersReducedMotion ||
      count < MIN_FOR_SWAP
    ) {
      return;
    }

    let intervalId = null;
    const tick = () => {
      setSpotlightIndex((prev) => (prev + 1) % count);
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(tick, SWAP_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    if (typeof document !== "undefined" && !document.hidden) {
      start();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [count, paused, clickPaused, prefersReducedMotion]);

  return { spotlightIndex, selectIndex };
}

// ---------------------------------------------------------------------------
// HeroSpotlightSwap main renderer
// ---------------------------------------------------------------------------

const HeroSpotlightSwap = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image"),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const isStack = isStackVariant(item?.hero_mode);
  const isStackFlush = isStackFlushVariant(item?.hero_mode);
  const clickToNavigate = isClickToNavigateVariant(item?.hero_mode);

  // Pick the right per-breakpoint config table for the chosen
  // layout. ``BP_CONFIG`` (horizontal split) and ``BP_STACK_CONFIG``
  // (vertical stack) declare different shapes — keeping them
  // separate (rather than one merged table with optional fields)
  // means downstream readers can read each table top-to-bottom
  // without holding the layout-switch in their head.
  const config = isStack
    ? BP_STACK_CONFIG[bp] || BP_STACK_CONFIG.medium
    : BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = !isStack && config.horizontal && hasImages;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  const { spotlightIndex, selectIndex } = useSpotlightRotation(images.length, {
    // Only the click-to-navigate variant needs the resume timer —
    // for the canonical horizontal variant we leave it at zero so
    // its (unused) ``selectIndex`` couldn't accidentally pause
    // auto-rotation if a future contributor wires it up without
    // updating the gating.
    resumeAfterMs: clickToNavigate ? CLICK_PAUSE_RESUME_MS : 0,
  });

  // Vertical break-out for the stack flush variant. The hero root
  // carries the user's spacing-driven padding (e.g. ``py-12``) and
  // without this the photo would sit ~48px below the hero's top
  // edge — which would leave a visible band of background framing
  // the photo and break the "flush" promise (the entire point of
  // the variant is edge-to-edge). We measure the hero root's
  // resolved vertical padding (see ``useHeroVerticalPadding``)
  // and pull the inner column up + down by exactly that amount so
  // the photo butts against the top edge and the text section's
  // bottom flows into the hero bottom edge.
  //
  // Same pattern ``HeroPhotoBanner`` uses for its strip-on-top
  // composition. The only difference: photo banner applies the
  // breakout to BOTH finishes (canonical + flush) because its
  // identity is "photos own the top of the hero" regardless; the
  // spotlight stack canonical variant deliberately keeps its
  // breathing room (rounded photo with margin) so we gate the
  // breakout on ``isStackFlush`` exclusively.
  //
  // Suppressed when ``responsiveHeight: false``: in that mode the
  // inner column is ``absolute inset-0`` and uses CSS positioning
  // rather than flex margins for placement, so negative margins
  // would stack with the inset values unpredictably. The padding
  // is part of the user's contract in fixed-height mode anyway.
  const heroPad = useHeroVerticalPadding(heroRootRef);
  const stackFlushBreakoutStyle =
    isStackFlush && responsiveHeight && (heroPad.top || heroPad.bottom)
      ? {
          marginTop: heroPad.top ? `-${heroPad.top}px` : 0,
          marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
        }
      : null;
  const spotlightImage = images[spotlightIndex] || images[0] || null;
  // Thumbnails are shown in source order; the active one gets a ring.
  // We deliberately don't reorder them when the spotlight rotates —
  // a moving thumbnail strip would compete with the spotlight itself
  // for attention. The user sees a stable strip with a moving accent.
  const thumbnails = images;

  // Forward EVERY background input from ``heroBase`` — including
  // ``jsPatternLayer`` — so a hero background composed of a colour
  // PLUS a JS-driven pattern (waves, grid, particles, …) renders
  // exactly the same here as it does in every other hero mode.
  // Why this matters: ``jsPatternLayer`` is the React node that
  // paints the pattern as a child of the colour shell (see
  // ``Background.js``); without it, only the underlying colour
  // gradient survives and the rotating-spotlight modes look like
  // they're forcing a "simple two-colour fade" background — a
  // visible regression compared to every other mode in the picker.
  // Every other hero renderer (``HeroSplit``, ``HeroCinematic``,
  // ``HeroMosaic``, ``HeroGallery``, ``HeroMagazineSwap``, …)
  // forwards this prop; this renderer was the only outlier.
  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  const spotlightSrc = spotlightImage
    ? spotlightImage.cropped_image_url || spotlightImage.image_url
    : null;

  // Stack-layout branch. Renders early because the JSX shape
  // diverges enough from the horizontal split (the photo column
  // is full-width on top, dots replace the thumbnail strip, the
  // content column lives in a separate row beneath rather than
  // alongside) that intermixing both shapes inside one return
  // would force a maze of conditional className strings — the
  // separate branch reads top-to-bottom for each layout instead.
  if (isStack) {
    // Inner-padding contract for the media column. Canonical
    // (rounded) variant carries ``p-3 sm:p-4`` so the rounded
    // stage sits inside a small breathing pocket that lets its
    // drop shadow soft-fade into the surrounding hero padding;
    // flush variant zeros that pocket out so the photo butts
    // straight against the column edges (paired with the
    // ``--flush`` modifier on the stage which strips the rounded
    // corners + shadow + border, AND with ``stackFlushBreakoutStyle``
    // above which absorbs the hero root's vertical padding so
    // the photo also butts against the hero top edge). Without
    // this the flush variant would still leave a 12-16px gutter
    // around the photo, making it visually indistinguishable
    // from the canonical variant.
    const stageWrapperPaddingClass = isStackFlush ? "p-0" : "p-3 sm:p-4";
    // Stage chrome flip. The ``--flush`` modifier in
    // ``heroSpotlightSwap.css`` removes the rounded corners,
    // drop shadow, and outer border so the photo reads as a
    // single rectangular slab edge-to-edge. ``rounded-2xl`` is
    // dropped from the canonical class string when flush so
    // Tailwind's utility doesn't fight the CSS reset (``border-
    // radius: 0`` from the modifier vs ``border-radius: 1rem``
    // from Tailwind's class — last-rule-wins is fragile here).
    const stageClass = isStackFlush
      ? "hero-spotlight-stage hero-spotlight-stage--flush relative flex-1 overflow-hidden"
      : "hero-spotlight-stage relative flex-1 overflow-hidden rounded-2xl";

    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />

        <div
          className={`hero-spotlight-stack flex flex-col w-full ${
            responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
          }`}
          style={{
            ...(responsiveHeight ? { minHeight: `${baseHeightPx}px` } : null),
            ...(stackFlushBreakoutStyle || {}),
          }}
          data-stack-layout="true"
        >
          {/* Photo column on top. Height is computed as a fixed
              pixel value derived from ``baseHeightPx`` times the
              per-breakpoint media percentage (same pattern
              ``HeroStack`` uses), rather than a flex-basis
              percentage. Why: a CSS percentage flex-basis only
              resolves against a parent with a *definite* height,
              and when ``responsiveHeight`` is enabled the parent
              only carries a ``min-height`` (no fixed ``height``).
              In that case ``flex: 0 0 75%`` collapses to 0 and
              the photo column disappears entirely — leaving only
              the text + dot indicator visible. Computing pixels
              up-front from the known ``baseHeightPx`` keeps the
              proportional split intact in both layout modes
              (responsive-height ON or OFF). */}
          {/* Media column. ``overflow-hidden`` is intentionally
              omitted here even though most other hero renderers
              clip their media columns: the spotlight stage carries
              an ``0 8px 24px`` drop shadow (see
              ``.hero-spotlight-stage`` in ``heroSpotlightSwap.css``)
              on the canonical variant, and clipping at this level
              chops the shadow's tail into a visible hard line
              right below the photo. The stage itself still has
              ``overflow-hidden rounded-2xl`` (canonical) or
              plain ``overflow-hidden`` (flush) so the actual
              photo content stays clipped to its frame; only the
              canonical variant's shadow is allowed to soft-fade
              into the surrounding pocket.

              ``relative`` is load-bearing — the dot indicator is
              now an absolutely-positioned overlay child of this
              column (see the dots block below), so the column
              has to be the positioning ancestor. Without
              ``relative`` the dots would escape and pin to the
              nearest positioned ancestor (the hero root or worse,
              the page), reading as a stray pill floating
              somewhere unrelated to the photo. */}
          <div
            className="hero-spotlight-stack-media relative flex-shrink-0"
            style={{
              width: "100%",
              height: `${Math.round((baseHeightPx * config.mediaHeightPct) / 100)}px`,
              minHeight: 0,
            }}
          >
            <div className={`absolute inset-0 ${stageWrapperPaddingClass} flex flex-col gap-3`}>
              <div className={stageClass}>
                {images.map((img, idx) => {
                  const src = img.cropped_image_url || img.image_url;
                  const isActive = idx === spotlightIndex;
                  const { className: filterClass, style: filterStyle } =
                    getHeroImageStyle(img);
                  return (
                    <div
                      key={`spotlight-${img.id || idx}-${idx}`}
                      className={`hero-spotlight-frame ${isActive ? "hero-spotlight-frame--active" : ""}`}
                    >
                      {src ? (
                        <ProgressiveHeroImage
                          src={src}
                          alt={img.alt_text || ""}
                          loading={idx === 0 ? "eager" : "lazy"}
                          draggable={false}
                          className={filterClass}
                          style={filterStyle}
                        />
                      ) : (
                        <div className="hero-spotlight-frame__empty" />
                      )}
                      <EditableSlotMediaOverlay slotData={img} heroItem={item} />
                    </div>
                  );
                })}
                {!spotlightSrc && images.length > 0 && (
                  <div className="hero-spotlight-frame__empty absolute inset-0" />
                )}
              </div>
            </div>

            {/* Dot indicator overlaid on the photo's bottom edge.
                Originally lived as a sibling row between the
                photo and the text column; the new home moves it
                INSIDE the photo as a translucent pill anchored
                bottom-center. Why the move: the row form took up
                its own vertical slice between the photo and the
                heading, which (a) added empty hero real estate
                between two zones the user wants visually tight
                and (b) read as a separate "controls" bar
                disconnected from the image. Overlaying the dots
                ON the photo turns the navigation affordance into
                a visible part of the image itself — same pattern
                most photo carousels on the web use, and the
                visitor's eye no longer has to jump between two
                zones to connect "click here" with "this photo".

                The ``--overlay`` modifier (in
                ``heroSpotlightSwap.css``) handles the
                positioning + the translucent backdrop pill +
                the blur so the dots stay readable on light AND
                dark photos without us having to gate on photo
                content.

                Skipped when only one image exists (nothing to
                navigate between; an isolated dot would read as
                decoration with no purpose) — same suppression
                rule the canonical horizontal variant uses for
                its thumbnail strip. */}
            {thumbnails.length > 1 && (
              <div
                className="hero-spotlight-dots hero-spotlight-dots--overlay"
                role="tablist"
                aria-label="Featured photo selector"
              >
                {thumbnails.map((img, idx) => {
                  const isActive = idx === spotlightIndex;
                  return (
                    <button
                      key={`spotlight-dot-${img.id || idx}-${idx}`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-label={`Show photo ${idx + 1} of ${thumbnails.length}`}
                      className={`hero-spotlight-dot ${isActive ? "hero-spotlight-dot--active" : ""}`}
                      onClick={() => selectIndex(idx)}
                    />
                  );
                })}
              </div>
            )}
            <SlotCapacityAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              currentCount={images.length}
              className="absolute inset-x-4 top-4"
            />
          </div>

          {/* Text column beneath the photo. ``flex-1`` so it
              consumes whatever room is left after the media
              column claims its fixed pixel height above;
              ``min-h-0`` so the inner text never forces the
              row taller than its share. */}
          <div
            ref={contentAnim.ref}
            className={`hero-spotlight-stack-content flex flex-col items-center justify-center gap-3 flex-1 min-h-0 w-full ${contentAnim.className}`}
            style={{
              paddingLeft: config.contentPx,
              paddingRight: config.contentPx,
              paddingTop: config.contentPy,
              paddingBottom: config.contentPy,
              ...contentAnim.style,
            }}
          >
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${isHorizontal ? "flex flex-row" : "flex flex-col"} w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Media column. Same ``min-height: 0`` + absolute wrapper
            sizing pattern HeroGallery uses, so the column never grows
            with its content (the spotlight image's intrinsic size
            could otherwise push the row taller).
            ``overflow-hidden`` is intentionally omitted (see the
            stack variant above for the full rationale): the stage's
            ``0 8px 24px`` drop shadow would get sliced into a hard
            line at this column's bottom edge otherwise. The stage
            itself still has ``overflow-hidden rounded-2xl`` so the
            photo content stays clipped to its rounded box; only the
            shadow is now allowed to soft-fade into the surrounding
            hero padding. */}
        <div
          className="relative flex-shrink-0"
          style={
            isHorizontal
              ? responsiveHeight
                ? {
                    // Keep the width, square the crop: a near-square
                    // aspect ratio grows the column's height while the
                    // ``minHeight`` floor keeps it at least as tall as
                    // the configured hero. ``flex-start`` stops the
                    // flex row from force-stretching the column back to
                    // the row height (which would cancel the ratio).
                    width: config.mediaPct,
                    alignSelf: "flex-start",
                    aspectRatio: HORIZONTAL_MEDIA_ASPECT,
                    minHeight: `${baseHeightPx}px`,
                  }
                : { width: config.mediaPct, minHeight: 0 }
              : {
                  width: "100%",
                  minHeight: config.mediaMaxH,
                  maxHeight: config.mediaMaxH,
                }
          }
        >
          <div className="absolute inset-0 p-3 sm:p-4 flex flex-col gap-3">
            <div className="hero-spotlight-stage relative flex-1 overflow-hidden rounded-2xl">
              {/* Render every spotlight candidate at once, layered.
                  The active one fades in via ``--active``; the rest
                  sit at opacity 0. Cross-fading two stacked elements
                  this way is what gives the rotation its smooth
                  "swap" rather than a hard cut. */}
              {images.map((img, idx) => {
                const src = img.cropped_image_url || img.image_url;
                const isActive = idx === spotlightIndex;
                const { className: filterClass, style: filterStyle } =
                  getHeroImageStyle(img);
                return (
                  <div
                    key={`spotlight-${img.id || idx}-${idx}`}
                    className={`hero-spotlight-frame ${isActive ? "hero-spotlight-frame--active" : ""}`}
                  >
                    {src ? (
                      <ProgressiveHeroImage
                        src={src}
                        alt={img.alt_text || ""}
                        loading={idx === 0 ? "eager" : "lazy"}
                        draggable={false}
                        className={filterClass}
                        style={filterStyle}
                      />
                    ) : (
                      <div className="hero-spotlight-frame__empty" />
                    )}
                    <EditableSlotMediaOverlay slotData={img} heroItem={item} />
                  </div>
                );
              })}
              {!spotlightSrc && images.length > 0 && (
                // Defensive: if every image is missing a URL we still
                // show a placeholder rather than a transparent void.
                <div className="hero-spotlight-frame__empty absolute inset-0" />
              )}
            </div>

            {thumbnails.length > 1 && (
              <div className="hero-spotlight-thumbs">
                {thumbnails.map((img, idx) => {
                  const src = img.cropped_image_url || img.image_url;
                  const isActive = idx === spotlightIndex;
                  const { className: filterClass, style: filterStyle } =
                    getHeroImageStyle(img);
                  return (
                    <div
                      key={`spotlight-thumb-${img.id || idx}-${idx}`}
                      className={`hero-spotlight-thumb ${isActive ? "hero-spotlight-thumb--active" : ""}`}
                      style={{ width: config.thumbSize, height: config.thumbSize }}
                    >
                      {src ? (
                        <ProgressiveHeroImage
                          src={src}
                          alt={img.alt_text || ""}
                          loading="lazy"
                          draggable={false}
                          className={filterClass}
                          style={filterStyle}
                        />
                      ) : (
                        <div className="hero-spotlight-frame__empty" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <SlotCapacityAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              currentCount={images.length}
            />
          </div>
        </div>

        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
          style={{
            ...(isHorizontal
              ? { paddingLeft: config.contentPx, paddingRight: config.contentPx }
              : { paddingLeft: config.contentPx, paddingRight: config.contentPx, paddingTop: config.contentPy, paddingBottom: config.contentPy }),
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroSpotlightSwap);
